import { Monitor, Moon, Sun } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme, type ThemeMode } from '../lib/theme';
import { patch } from '../lib/api';
import { setMeIfCurrent } from '../lib/session';
import type { Me } from '@workdesk/shared/contracts';
import { cn } from '../lib/cn';
import { isImpersonating } from '../lib/impersonation';

const OPTIONS: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: 'system', label: 'System', Icon: Monitor },
  { mode: 'light', label: 'Light', Icon: Sun },
  { mode: 'dark', label: 'Dark', Icon: Moon },
];

/**
 * System / Light / Dark segmented control (§11b). `sync` saves the choice to the user (Me screen, top bar);
 * on the login screen it is local only.
 */
export function ThemeSegmented({ sync, className }: { sync?: boolean; className?: string }) {
  const { mode, setTheme } = useTheme();
  const qc = useQueryClient();
  const choose = (m: ThemeMode) => {
    setTheme(m);
    if (sync && !isImpersonating()) {
      void patch<Me>('/api/me', { theme: m })
        .then((me) => setMeIfCurrent(qc, me))
        .catch(() => undefined);
    }
  };
  return (
    <div role="radiogroup" aria-label="Theme" className={cn('inline-flex rounded-lg border border-border bg-surface-alt p-0.5', className)}>
      {OPTIONS.map(({ mode: m, label, Icon }) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={mode === m}
          onClick={() => choose(m)}
          className={cn(
            'inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm transition-colors duration-150',
            mode === m ? 'bg-card font-semibold text-ink shadow-[0_1px_2px_rgb(20_22_27/0.08)]' : 'text-muted hover:text-ink',
          )}
        >
          <Icon aria-hidden className="size-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

/** Compact icon toggle for the top bar: cycles System → Light → Dark. */
export function ThemeIconButton({ sync = true }: { sync?: boolean }) {
  const { mode, dark, setTheme } = useTheme();
  const qc = useQueryClient();
  const next: ThemeMode = mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system';
  const Icon = mode === 'system' ? Monitor : dark ? Moon : Sun;
  const label = `Theme: ${mode === 'system' ? 'System' : mode === 'light' ? 'Light' : 'Dark'}. Switch to ${next}`;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        setTheme(next);
        if (sync && !isImpersonating()) void patch<Me>('/api/me', { theme: next }).then((me) => setMeIfCurrent(qc, me)).catch(() => undefined);
      }}
      className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-ink-2 hover:bg-surface hover:text-ink"
    >
      <Icon aria-hidden className="size-5" />
    </button>
  );
}
